import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Shield, Zap, CheckCircle, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { fireEvent } from '@/lib/analytics';
import PrefilledBadge from '@/components/PrefilledBadge';
import CtaForm from '@/components/CtaForm';

export default function BuyBIN() {
  const [searchParams] = useSearchParams();
  const domain = searchParams.get('domain');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (domain) {
      fireEvent('cta_page_view', { type: 'bin', domain });
    }
  }, [domain]);

  if (!domain) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="apple-card">
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2">Domain Required</h1>
              <p className="text-muted-foreground mb-6">
                No domain specified. Please select a domain from our inventory first.
              </p>
              <Link to="/domains">
                <Button>Browse Domains</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const handleProceedToCheckout = () => {
    setIsProcessing(true);

    fireEvent('cta_submit', { type: 'bin', domain });

    setTimeout(() => {
      toast({
        title: 'Redirecting to secure checkout',
        description: "You'll be taken to a trusted payment provider to complete your purchase.",
      });

      setIsProcessing(false);
    }, 2000);
  };

  const scrollToFAQ = () => {
    toast({
      title: 'Questions?',
      description: 'Check our FAQ section or contact me directly.',
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="mb-4">
          <Link
            to={`/domain/${domain}`}
            className="inline-flex items-center text-muted-foreground hover:text-primary transition-colors cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to {domain}
          </Link>
        </div>

        {/* Header */}
        <Card className="apple-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-3xl font-bold">
              Buy {domain} now—fast, safe, supported
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <PrefilledBadge domain={domain} />

            <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-6">
              <p className="text-lg font-medium mb-4">
                This info page is your last step before checkout.
              </p>
              <p className="text-muted-foreground">
                Review the process below, then click "Proceed to secure checkout" when you're ready
                to own this domain.
              </p>
            </div>

            {/* 3-Step Process */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold">Simple 3-step process:</h3>
              <div className="grid gap-4">
                <div className="flex items-start gap-4 p-4 bg-muted/30 rounded-lg">
                  <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-sm">
                    1
                  </div>
                  <div>
                    <h4 className="font-semibold">Pay securely via a trusted payment provider</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Your funds are protected until you confirm receipt of the domain
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-muted/30 rounded-lg">
                  <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-sm">
                    2
                  </div>
                  <div>
                    <h4 className="font-semibold">Guided transfer</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      We handle all the paperwork and guide you through each step
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-muted/30 rounded-lg">
                  <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-sm">
                    3
                  </div>
                  <div>
                    <h4 className="font-semibold">You own it</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Domain transfers to your account, typically within hours
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="bg-muted/20 rounded-lg p-6">
              <h3 className="font-semibold mb-4">Why buy with confidence:</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="flex flex-col items-center text-center">
                  <Shield className="w-8 h-8 text-primary mb-2" />
                  <h4 className="font-semibold text-sm">Trusted payment provider</h4>
                  <p className="text-xs text-muted-foreground">Industry standard security</p>
                </div>
                <div className="flex flex-col items-center text-center">
                  <Clock className="w-8 h-8 text-primary mb-2" />
                  <h4 className="font-semibold text-sm">Hours not weeks</h4>
                  <p className="text-xs text-muted-foreground">Fast transfer process</p>
                </div>
                <div className="flex flex-col items-center text-center">
                  <Zap className="w-8 h-8 text-primary mb-2" />
                  <h4 className="font-semibold text-sm">Direct support</h4>
                  <p className="text-xs text-muted-foreground">Personal assistance</p>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="space-y-3 pt-4">
              <Button
                onClick={handleProceedToCheckout}
                disabled={isProcessing}
                size="lg"
                className="w-full"
              >
                {isProcessing ? (
                  <>
                    <div className="w-4 h-4 mr-2 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    Redirecting to secure checkout...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-2" />
                    Proceed to secure checkout
                  </>
                )}
              </Button>

              <Button variant="outline" onClick={scrollToFAQ} className="w-full">
                Questions before you buy?
              </Button>
            </div>

            <div className="text-center pt-4">
              <p className="text-xs text-muted-foreground">
                Clicking "Proceed" will redirect you to a trusted payment provider for secure
                payment processing. I pay all escrow fees on your behalf.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Express Interest Form */}
        <Card className="apple-card">
          <CardHeader>
            <CardTitle>Express Interest</CardTitle>
            <p className="text-muted-foreground">
              Let us know you're interested and we'll follow up with next steps.
            </p>
          </CardHeader>
          <CardContent>
            <CtaForm
              pageType="bin"
              subjectPrefix="[BIN INTENT]"
              onSuccess={() => {
                toast({
                  title: 'Interest recorded!',
                  description: "We'll follow up shortly with next steps for securing this domain.",
                });
              }}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
